import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, CheckCheck } from "lucide-react";
import AppShell from "../components/AppShell";
import { Spinner } from "../components/ui";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function NotificationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");

  const fetchList = async (unreadOnly = false) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/notifications", {
        params: { limit: 50, unread_only: unreadOnly },
      });
      setItems(data || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(filter === "unread");
  }, [filter]);

  const markOne = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
    } catch (_) {}
  };

  const markAll = async () => {
    try {
      await api.post("/api/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch (_) {}
  };

  const bookingLink = (n) => {
    if (n.reference_type !== "booking" || !n.reference_id) return null;
    const base = user?.role === "WORKER" ? "/worker/bookings" : "/customer/bookings";
    return `${base}/${n.reference_id}`;
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl md:text-3xl text-brand-800 flex items-center gap-2">
          <BellRing className="h-6 w-6 text-brand-600" /> Notifikasi
        </h1>
        <button
          onClick={markAll}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-brand-600 hover:bg-brand-50"
          data-testid="notif-read-all"
        >
          <CheckCheck className="h-4 w-4" /> Tandai semua dibaca
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setFilter("all")}
          className={`chip ${filter === "all" ? "bg-brand-600 text-white" : "bg-brand-50 text-brand-600"}`}
        >
          Semua
        </button>
        <button
          onClick={() => setFilter("unread")}
          className={`chip ${filter === "unread" ? "bg-brand-600 text-white" : "bg-brand-50 text-brand-600"}`}
        >
          Belum dibaca
        </button>
      </div>

      {loading && <Spinner />}
      {err && <p className="text-sm text-red-600">{err}</p>}
      {!loading && !err && items.length === 0 && (
        <div className="card p-8 text-center text-brand-400">Belum ada notifikasi.</div>
      )}
      <div className="space-y-2">
        {items.map((n) => {
          const link = bookingLink(n);
          return (
            <div
              key={n.id}
              className={`card p-4 flex items-start gap-3 ${!n.read_at ? "border-brand-300 bg-brand-50/40" : ""}`}
              data-testid={`notif-item-${n.read_at ? "read" : "unread"}`}
            >
              <div className="flex-1">
                <p className="font-bold text-brand-800 text-sm">
                  [{n.type}] {n.title}
                </p>
                <p className="text-sm text-brand-600 mt-0.5">{n.message}</p>
                <p className="text-xs text-brand-300 mt-1">
                  {n.created_at ? new Date(n.created_at).toLocaleString("id-ID") : ""}
                </p>
                {link && (
                  <Link to={link} className="text-xs font-semibold text-brand-600 hover:underline">
                    Lihat pesanan →
                  </Link>
                )}
              </div>
              {!n.read_at && (
                <button
                  onClick={() => markOne(n.id)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                >
                  Tandai dibaca
                </button>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
